<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import TrendMetricCard from 'dashboard/components-next/Charts/TrendMetricCard.vue';

const { t } = useI18n();
const store = useStore();

const accountSummary = useMapGetter('getAccountSummary');
const fetchingStatus = useMapGetter('getAccountSummaryFetchingStatus');

const isLoading = computed(() => fetchingStatus.value === 'fetching');

const getStartOfToday = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.floor(now.getTime() / 1000);
};

const getEndOfToday = () => {
  const now = new Date();
  now.setHours(23, 59, 59, 999);
  return Math.floor(now.getTime() / 1000);
};

const fetchData = () => {
  const today = getStartOfToday();
  const endToday = getEndOfToday();

  store.dispatch('fetchAccountSummary', {
    from: today,
    to: endToday,
    type: 'account',
    groupBy: 'day',
  });
};

const { startRefetching } = useLiveRefresh(fetchData, 60000);

const resolutionsToday = computed(
  () => accountSummary.value.resolutions_count || 0
);
const avgFirstResponseTime = computed(
  () => accountSummary.value.avg_first_response_time || 0
);
const avgResolutionTime = computed(
  () => accountSummary.value.avg_resolution_time || 0
);
const conversationsCount = computed(
  () => accountSummary.value.conversations_count || 0
);

const resolutionRate = computed(() => {
  if (conversationsCount.value === 0) return 0;
  return (resolutionsToday.value / conversationsCount.value) * 100;
});

const previousResolutions = computed(
  () => accountSummary.value.previous?.resolutions_count || null
);
const previousFirstResponse = computed(
  () => accountSummary.value.previous?.avg_first_response_time || null
);
const previousResolutionTime = computed(
  () => accountSummary.value.previous?.avg_resolution_time || null
);
const previousConversations = computed(
  () => accountSummary.value.previous?.conversations_count || null
);

const previousResolutionRate = computed(() => {
  if (!previousConversations.value || !previousResolutions.value) return null;
  return (previousResolutions.value / previousConversations.value) * 100;
});

// Sparklines disabled - would need real hourly/daily trend data from backend
// For now we show metrics without sparklines

const metrics = computed(() => [
  {
    title: t('REPORT.METRICS.RESOLUTION_COUNT.NAME'),
    value: resolutionsToday.value,
    previousValue: previousResolutions.value,
    format: 'number',
    icon: 'i-lucide-check-circle-2',
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-500/10',
    sparklineColor: '#10b981',
    sparklineData: [],
  },
  {
    title: t('REPORT.METRICS.FIRST_RESPONSE_TIME.NAME'),
    value: avgFirstResponseTime.value,
    previousValue: previousFirstResponse.value,
    format: 'time',
    icon: 'i-lucide-timer',
    iconColor: 'text-amber-600',
    iconBg: 'bg-amber-500/10',
    sparklineColor: '#f59e0b',
    invertTrend: true,
    sparklineData: [],
  },
  {
    title: t('REPORT.METRICS.RESOLUTION_TIME.NAME'),
    value: avgResolutionTime.value,
    previousValue: previousResolutionTime.value,
    format: 'time',
    icon: 'i-lucide-clock',
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-500/10',
    sparklineColor: '#3b82f6',
    invertTrend: true,
    sparklineData: [],
  },
  {
    title: t('REPORT.RESOLUTION_RATE'),
    value: resolutionRate.value,
    previousValue: previousResolutionRate.value,
    format: 'percent',
    icon: 'i-lucide-percent',
    iconColor: 'text-violet-600',
    iconBg: 'bg-violet-500/10',
    sparklineColor: '#8b5cf6',
    sparklineData: [],
  },
]);

onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
    <TrendMetricCard
      v-for="(metric, index) in metrics"
      :key="index"
      :title="metric.title"
      :value="metric.value"
      :previous-value="metric.previousValue"
      :format="metric.format"
      :icon="metric.icon"
      :icon-color="metric.iconColor"
      :icon-bg="metric.iconBg"
      :sparkline-data="metric.sparklineData"
      :sparkline-color="metric.sparklineColor"
      :invert-trend="metric.invertTrend"
      :loading="isLoading"
    />
  </div>
</template>
